import React, {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ChartComponent, { type ChartComponentProps } from "react-chartjs-2";
import Chart from "chart.js";
import { addCommas } from "Util/Utils";
import { ReactifyChartColors } from "Util/ReactifyChartColors";

/** Chart instance with optional pointer state set by `centerTextPlugin.beforeEvent`. */
type ChartWithPointer = Chart & {
  pointIndex?: number;
  pointDataIndex?: number;
  pointAvailable?: boolean;
};

/** Chart.js v2 plugin `chart` argument (nested `chart` + runtime legend/meta). */
type CenterTextChart = ChartWithPointer & {
  chart: { ctx: CanvasRenderingContext2D };
  legend: { legendItems: Array<{ hidden?: boolean }> };
};

type ChartDatasetWithMeta = Chart.ChartDataSets & {
  _meta?: Record<string, { total?: number | string }>;
};

type ChartElementAtEvent = {
  _index: number;
  _datasetIndex: number;
};


export const chartTooltip = {
  backgroundColor: ReactifyChartColors().foregroundColor,
  titleFontColor: ReactifyChartColors().primaryColor,
  borderColor: ReactifyChartColors().separatorColor,
  borderWidth: 0.5,
  bodyFontColor: ReactifyChartColors().primaryColor,
  bodySpacing: 10,
  xPadding: 15,
  yPadding: 15,
  cornerRadius: 0.15
};

export const centerTextPlugin = {
  afterDatasetsUpdate: function(_chart: ChartWithPointer) {},
  beforeDraw: function(chart: CenterTextChart) {
    var width = chart.chartArea.right;
    var height = chart.chartArea.bottom;
    var ctx = chart.chart.ctx;
    ctx.restore();

    const labels = chart.data.labels;
    const datasets = chart.data.datasets;
    if (!labels?.length || !datasets?.length) {
      return;
    }

    var activeLabel: unknown = labels[0];
    var dataset = datasets[0] as ChartDatasetWithMeta;
    var dataArr = dataset.data;
    var activeValue = Array.isArray(dataArr) ? dataArr[0] : dataArr;
    var metaKeys = Object.keys(dataset._meta ?? {});
    var meta = metaKeys.length ? dataset._meta![metaKeys[0]] : undefined;
    var total = Number(meta?.total ?? 0);

    var activePercentage = parseFloat(
      ((Number(activeValue) / total) * 100).toFixed(1)
    );
    activePercentage = chart.legend.legendItems[0].hidden
      ? 0
      : activePercentage;

    if (
      chart.pointAvailable &&
      chart.pointIndex !== undefined &&
      chart.pointDataIndex !== undefined
    ) {
      activeLabel = labels[chart.pointIndex];
      const ds = datasets[chart.pointDataIndex] as ChartDatasetWithMeta;
      dataset = ds;
      const ptData = ds.data;
      const arr = Array.isArray(ptData) ? ptData : [];
      activeValue = arr[chart.pointIndex];
      metaKeys = Object.keys(dataset._meta ?? {});
      meta = metaKeys.length ? dataset._meta![metaKeys[0]] : undefined;
      total = Number(meta?.total ?? 0);
      activePercentage = parseFloat(
        ((Number(activeValue) / total) * 100).toFixed(1)
      );
      activePercentage = chart.legend.legendItems[chart.pointIndex].hidden
        ? 0
        : activePercentage;
    }

    ctx.font = "36px Nunito, sans-serif";
    ctx.fillStyle = ReactifyChartColors().primaryColor;
    ctx.textBaseline = "middle";

    var text = activePercentage + "%",
      textX = Math.round((width - ctx.measureText(text).width) / 2),
      textY = height / 2;
    ctx.fillText(text, textX, textY);

    ctx.font = "14px Nunito, sans-serif";
    ctx.textBaseline = "middle";

    var text2 = String(activeLabel),
      textX2 = Math.round((width - ctx.measureText(text2).width) / 2),
      textY2 = height / 2 - 30;
    ctx.fillText(text2, textX2, textY2);

    ctx.save();
  },
  beforeEvent: function(chart: ChartWithPointer, event: unknown) {
    var firstPoint = chart.getElementAtEvent(event)[0] as
      | ChartElementAtEvent
      | undefined;

    if (firstPoint) {
      chart.pointIndex = firstPoint._index;
      chart.pointDataIndex = firstPoint._datasetIndex;
      chart.pointAvailable = true;
    }
  }
};

let polarShadowRegistered = false;
function ensurePolarShadowRegistered() {
  if (polarShadowRegistered) return;
  polarShadowRegistered = true;
  Chart.defaults.polarWithShadow = Chart.defaults.polarArea;
  Chart.controllers.polarWithShadow = Chart.controllers.polarArea.extend({
    draw: function(ease: unknown) {
      Chart.controllers.radar.prototype.draw.call(this, ease);
      let ctx = this.chart.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 7;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 7;
      ctx.responsive = true;
      Chart.controllers.radar.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function PolarShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensurePolarShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"polarWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let lineShadowRegistered = false;
function ensureLineShadowRegistered() {
  if (lineShadowRegistered) return;
  lineShadowRegistered = true;
  Chart.defaults.lineWithShadow = Chart.defaults.line;
  Chart.controllers.lineWithShadow = Chart.controllers.line.extend({
    draw: function(ease: unknown) {
      Chart.controllers.line.prototype.draw.call(this, ease);
      var ctx = this.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.15)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      ctx.responsive = true;
      ctx.stroke();
      Chart.controllers.line.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function LineShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensureLineShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"lineWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let scatterShadowRegistered = false;
function ensureScatterShadowRegistered() {
  if (scatterShadowRegistered) return;
  scatterShadowRegistered = true;
  Chart.defaults.scatterWithShadow = Chart.defaults.scatter;
  Chart.controllers.scatterWithShadow = Chart.controllers.scatter.extend({
    draw: function(ease: unknown) {
      Chart.controllers.scatter.prototype.draw.call(this, ease);
      let ctx = this.chart.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 7;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 7;
      ctx.responsive = true;
      Chart.controllers.scatter.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function ScatterShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensureScatterShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"scatterWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let barShadowRegistered = false;
function ensureBarShadowRegistered() {
  if (barShadowRegistered) return;
  barShadowRegistered = true;
  Chart.defaults.barWithShadow = Chart.defaults.bar;
  Chart.controllers.barWithShadow = Chart.controllers.bar.extend({
    draw: function(ease: unknown) {
      Chart.controllers.bar.prototype.draw.call(this, ease);
      var ctx = this.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 7;
      ctx.shadowOffsetX = 5;
      ctx.shadowOffsetY = 7;
      ctx.responsive = true;
      Chart.controllers.bar.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function BarShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensureBarShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"barWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let radarShadowRegistered = false;
function ensureRadarShadowRegistered() {
  if (radarShadowRegistered) return;
  radarShadowRegistered = true;
  Chart.defaults.radarWithShadow = Chart.defaults.radar;
  Chart.controllers.radarWithShadow = Chart.controllers.radar.extend({
    draw: function(ease: unknown) {
      Chart.controllers.radar.prototype.draw.call(this, ease);
      let ctx = this.chart.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 7;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 7;
      ctx.responsive = true;
      Chart.controllers.radar.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function RadarShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensureRadarShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"radarWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let pieShadowRegistered = false;
function ensurePieShadowRegistered() {
  if (pieShadowRegistered) return;
  pieShadowRegistered = true;
  Chart.defaults.pieWithShadow = Chart.defaults.pie;
  Chart.controllers.pieWithShadow = Chart.controllers.pie.extend({
    draw: function(ease: unknown) {
      Chart.controllers.pie.prototype.draw.call(this, ease);
      let ctx = this.chart.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.15)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      ctx.responsive = true;
      Chart.controllers.pie.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function PieShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensurePieShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"pieWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let doughnutShadowRegistered = false;
function ensureDoughnutShadowRegistered() {
  if (doughnutShadowRegistered) return;
  doughnutShadowRegistered = true;
  Chart.defaults.doughnutWithShadow = Chart.defaults.doughnut;
  Chart.controllers.doughnutWithShadow = Chart.controllers.doughnut.extend({
    draw: function(ease: unknown) {
      Chart.controllers.doughnut.prototype.draw.call(this, ease);
      let ctx = this.chart.chart.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.15)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      ctx.responsive = true;
      Chart.controllers.doughnut.prototype.draw.apply(this, arguments);
      ctx.restore();
    }
  });
}

export function DoughnutShadow(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  useLayoutEffect(() => {
    ensureDoughnutShadowRegistered();
  }, []);
  return (
    <ChartComponent
      ref={ref => {
        chartInstanceRef.current =
          (ref?.chartInstance as Chart | undefined) ?? null;
      }}
      type={"doughnutWithShadow" as Chart.ChartType}
      {...props}
    />
  );
}

let lineWithLineRegistered = false;
function ensureLineWithLineRegistered() {
  if (lineWithLineRegistered) return;
  lineWithLineRegistered = true;
  Chart.defaults.lineWithLine = Chart.defaults.line;
  Chart.controllers.lineWithLine = Chart.controllers.line.extend({
    draw: function(ease: unknown) {
      Chart.controllers.line.prototype.draw.call(this, ease);

      if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
        var activePoint = this.chart.tooltip._active[0];
        var ctx = this.chart.ctx;
        var x = activePoint.tooltipPosition().x;
        var topY = this.chart.scales["y-axis-0"].top;
        var bottomY = this.chart.scales["y-axis-0"].bottom;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(0,0,0,0.1)";
        ctx.stroke();
        ctx.restore();
      }
    }
  });
}

export function SmallLineChart(props: ChartComponentProps) {
  const chartInstanceRef = useRef<Chart | null>(null);
  const [currentValue, setCurrentValue] = useState("");
  const [currentLabel, setCurrentLabel] = useState("");

  const changeState = useCallback(
    (yLabel: string | number, xLabel: string | number) => {
      setCurrentValue(String(yLabel));
      setCurrentLabel(String(xLabel));
    },
    []
  );

  useLayoutEffect(() => {
    ensureLineWithLineRegistered();
  }, []);

  const options = useMemo(
    () => ({
      layout: {
        padding: {
          left: 5,
          right: 5,
          top: 10,
          bottom: 10
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true
            },
            display: false
          }
        ],
        xAxes: [
          {
            display: false
          }
        ]
      },
      tooltips: {
        intersect: false,
        enabled: false,
        custom: function(tooltipModel: Chart.ChartTooltipModel) {
          if (tooltipModel && tooltipModel.dataPoints) {
            var yLabel = tooltipModel.dataPoints[0].yLabel;
            var xLabel = tooltipModel.dataPoints[0].xLabel;
            var label = tooltipModel.body[0].lines[0].split(":")[0];
            changeState(
              "$" + addCommas(yLabel ?? ""),
              label + "-" + (xLabel ?? "")
            );
          }
        }
      }
    }),
    [changeState]
  );

  const plugins = useMemo(
    () => [
      {
        afterInit: function(chart: Chart) {
          var yLabel = chart.data.datasets![0].data![0];
          var xLabel = chart.data.labels![0];
          var label = chart.data.datasets![0].label;
          changeState("$" + addCommas(yLabel as string | number), String(label) + "-" + xLabel);
        }
      }
    ],
    [changeState]
  );

  return (
    <Fragment>
      <div>
        <p className="lead color-theme-1 mb-1 value">{currentValue}</p>
        <p className="mb-0 label text-small">{currentLabel}</p>
      </div>
      <div className="chart">
        <ChartComponent
          ref={ref => {
            chartInstanceRef.current =
              (ref?.chartInstance as Chart | undefined) ?? null;
          }}
          type={"lineWithLine" as Chart.ChartType}
          options={options}
          plugins={plugins}
          {...props}
        />
      </div>
    </Fragment>
  );
}

/**
 * Chart.js looks up controllers by `type` when the chart is constructed.
 * `react-chartjs-2` v2 creates the Chart in the child's `componentDidMount`, which
 * runs before a *parent* `useLayoutEffect` — so registering in `useLayoutEffect`
 * on `LineShadow` / `BarShadow` is too late. Register once at module init instead.
 */
function registerAllChartJsShadowControllers() {
  ensurePolarShadowRegistered();
  ensureLineShadowRegistered();
  ensureScatterShadowRegistered();
  ensureBarShadowRegistered();
  ensureRadarShadowRegistered();
  ensurePieShadowRegistered();
  ensureDoughnutShadowRegistered();
  ensureLineWithLineRegistered();
}

registerAllChartJsShadowControllers();
